import React, { useCallback, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CONTACT } from "@/constants/testIds";
import { createSchedule } from "@/lib/api";
import { toast } from "sonner";

const INITIAL = {
  nombre: "",
  email: "",
  telefono: "",
  fecha_preferida: "",
  horario_preferido: "",
  tema: "",
  mensaje: "",
};

const HORARIOS = [
  { v: "manana", label: "Mañana (09:00 – 12:00)" },
  { v: "mediodia", label: "Mediodía (12:00 – 15:00)" },
  { v: "tarde", label: "Tarde (15:00 – 19:00)" },
];

const TEMAS = [
  { v: "hogar", label: "Hogar" },
  { v: "vida", label: "Vida" },
  { v: "salud", label: "Salud" },
  { v: "automovil", label: "Automóvil" },
  { v: "ahorro", label: "Ahorro" },
  { v: "inversiones", label: "Inversiones" },
  { v: "empresas", label: "Empresas" },
  { v: "otro", label: "Otro" },
];

const underlineCls =
  "mt-2 border-0 border-b border-razmek rounded-none focus-visible:ring-0 focus-visible:border-razmek-gold focus:ring-0 px-0 font-sans";

const validate = (form) => !!form.nombre && !!form.email;

const ContactForm = ({ onSuccess }) => {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  const update = useCallback(
    (k, v) => setForm((f) => ({ ...f, [k]: v })),
    []
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validate(form)) {
        toast.error("Por favor completa tu nombre y correo.");
        return;
      }
      setSubmitting(true);
      try {
        await createSchedule(form);
        toast.success("Solicitud recibida. Te contactaremos pronto.");
        onSuccess();
      } catch (err) {
        toast.error("Hubo un problema. Inténtalo nuevamente.");
      } finally {
        setSubmitting(false);
      }
    },
    [form, onSuccess]
  );

  return (
    <form
      data-testid={CONTACT.form}
      onSubmit={handleSubmit}
      className="lg:col-span-8 bg-white border border-razmek p-8 lg:p-12 space-y-8"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label className="eyebrow text-razmek-gray">Nombre completo *</Label>
          <Input
            data-testid={CONTACT.field("nombre")}
            value={form.nombre}
            onChange={(e) => update("nombre", e.target.value)}
            className={underlineCls}
          />
        </div>
        <div>
          <Label className="eyebrow text-razmek-gray">Teléfono</Label>
          <Input
            data-testid={CONTACT.field("telefono")}
            value={form.telefono}
            onChange={(e) => update("telefono", e.target.value)}
            className={underlineCls}
          />
        </div>
        <div className="md:col-span-2">
          <Label className="eyebrow text-razmek-gray">Correo electrónico *</Label>
          <Input
            data-testid={CONTACT.field("email")}
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            className={underlineCls}
          />
        </div>
        <div>
          <Label className="eyebrow text-razmek-gray">Fecha preferida</Label>
          <Input
            data-testid={CONTACT.field("fecha")}
            type="date"
            value={form.fecha_preferida}
            onChange={(e) => update("fecha_preferida", e.target.value)}
            className={underlineCls}
          />
        </div>
        <div>
          <Label className="eyebrow text-razmek-gray">Horario</Label>
          <Select
            value={form.horario_preferido}
            onValueChange={(v) => update("horario_preferido", v)}
          >
            <SelectTrigger data-testid={CONTACT.field("horario")} className={underlineCls}>
              <SelectValue placeholder="Selecciona" />
            </SelectTrigger>
            <SelectContent>
              {HORARIOS.map((h) => (
                <SelectItem key={h.v} value={h.v}>
                  {h.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="eyebrow text-razmek-gray">Tema de interés</Label>
          <Select value={form.tema} onValueChange={(v) => update("tema", v)}>
            <SelectTrigger data-testid={CONTACT.field("tema")} className={underlineCls}>
              <SelectValue placeholder="Selecciona un tema" />
            </SelectTrigger>
            <SelectContent>
              {TEMAS.map((t) => (
                <SelectItem key={t.v} value={t.v}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="eyebrow text-razmek-gray">Mensaje</Label>
          <Textarea
            data-testid={CONTACT.field("mensaje")}
            value={form.mensaje}
            onChange={(e) => update("mensaje", e.target.value)}
            rows={4}
            className="mt-2 border border-razmek rounded-none focus-visible:ring-0 focus-visible:border-razmek-gold font-sans resize-none"
          />
        </div>
      </div>

      <Button
        data-testid={CONTACT.submit}
        type="submit"
        disabled={submitting}
        className="w-full bg-razmek-blue hover:bg-razmek-dark text-white rounded-none font-sans tracking-[0.16em] uppercase text-[11px] py-6 border-2 border-razmek-blue hover:border-razmek-gold transition-all"
      >
        {submitting ? "Enviando…" : "Solicitar asesoría"}
      </Button>

      <p className="font-sans text-xs text-razmek-gray text-center">
        Al enviar aceptas nuestro aviso de privacidad. Tus datos sólo serán
        usados por nuestro equipo de asesores.
      </p>
    </form>
  );
};

export default ContactForm;
